import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  P500UIButtonsModule,
  P500UIFormFieldsModule,
  P500UIIconsModule,
  P500UIManyItemsModule,
  P500UIPipesModule,
  P500UITooltipModule,
} from '@platform500services/p500-ui-kit';
import { ActionButtonDirectiveModule } from '../../directives/action-button/action-button.module';
import { BooleanComponent } from './default/boolean/boolean.component';
import { CountriesSelectComponent } from './default/countries-select/countries-select.component';
import { CountriesComponent } from './default/countries/countries.component';
import { CountryComponent } from './default/country/country.component';
import { DatetimeComponent } from './default/datetime/datetime.component';
import { DefaultEmptyComponent } from './default/empty/default-empty.component';
import { LinkComponent } from './default/link/link.component';
import { ManyItemsComponent } from './default/many-items/many-items.component';
import { MoneyComponent } from './default/money/money.component';
import { StatusComponent } from './default/status/status.component';
import { StringOrDefaultComponent } from './default/string-or-default/string-or-default.component';
import { StringOrEmptyComponent } from './default/string-or-empty/string-or-empty.component';
import { StringWhitespaceOrEmptyWithCopyComponent } from './default/string-whitespace-or-empty-with-copy/string-whitespace-or-empty-with-copy.component';
import { StringWhitespaceOrEmptyComponent } from './default/string-whitespace-or-empty/string-whitespace-or-empty.component';
import { StringComponent } from './default/string/string.component';
import { ViewModeComponent } from './default/view-mode/view-mode.component';
import { DynamicComponent } from './dynamic/dynamic/dynamic.component';
import { AffiliatesTrackersTypeComponent } from './special/affiliates-trackers-type/affiliates-trackers-type.component';
import { ConversionTrackingTypeComponent } from './special/conversion-tracking-type/conversion-tracking-type.component';
import { GoalPayoutTypeComponent } from './special/goal-payout-type/goal-payout-type.component';
import { GoalRevenueTypeComponent } from './special/goal-revenue-type/goal-revenue-type.component';
import { OfferConversionTypeComponent } from './special/offer-conversion-type/offer-conversion-type.component';
import { ReportingPresetsDrilldownModalComponent } from './special/reporting-presets-drilldown-modal/reporting-presets-drilldown-modal.component';
import { AffiliatePdfComponent } from './special/affiliate-pdf/affiliate-pdf.component';
import { InvoiceStatusComponent } from './default/invoice-status/invoice-status.component';
import { CurrencyComponent } from './default/currency/currency.component';
import { AuthAsComponent } from './special/auth-as/auth-as.component';
import { ReportingPresetsReportBuilderModalComponent } from './special/reporting-presets-report-builder-modal/reporting-presets-report-builder-modal.component';
import { ProtectedFieldComponent } from './default/protected/protected.component';
import { NotificationTypeComponent } from './special/notification-type/notification-type.component';
import { IncidentStatusComponent } from './special/incident-status/incident-status.component';
import { AuthorComponent } from './default/author/author.component';
import { NumberFieldDynamicHintComponent } from './default/number-field-dynamic-hint/number-field-dynamic-hint.component';
import { BusinessFormatComponent } from './default/business-format/business-format.component';
import { BusinessModelFormatComponent } from './default/business-model-format/business-model-format.component';
import { LanguageComponent } from './default/language/language.component';
import { NullableSelectComponent } from './default/nullable-select/nullable-select.component';
import { AffiliateLinkWithAllComponent } from './default/affiliate-link-with-all/affiliate-link-with-all.component';
import { RbacDirectiveModule } from '../../directives/rbac/rbac-directive.module';
import { LanguagesComponent } from './default/languages/languages.component';
import { NotificationDescriptionModule } from '../notification-description/notification-description.module';
import { CrmCurrencyComponent } from './default/crm-currency/crm-currency.component';
import { TreeMoneyFormatComponent } from './special/tree-money-format/tree-money-format.component';
import { BaselineCumulativeComponent } from './special/baseline-cumulative/baseline-cumulative.component';
import { AffiliateAnswersComponent } from './special/affiliate-answers/affiliate-answers.component';
import { NumberComponent } from './default/number/number.component';
import { PostbackStatusComponent } from './default/postback-status/postback-status.component';
import { PercentComponent } from './default/percent/percent.component';
import { GoalTypesComponent } from './default/goal-types/goal-types.component';
import { CrmPercentComponent } from './default/crm-percent/crm-percent.component';
import { TermsFormatComponent } from './default/terms-format/terms-format.component';
import { OfferUrlSourceComponent } from './default/offer-url-source/offer-url-source.component';
import { StringListProtectedComponent } from './default/string-list-protected/string-list-protected.component';
import { LinksComponent } from './default/links/links.component';
import { NewPqlTypeFreeComponent } from './special/tree-preview/free/free.component';
import { TreePayloadComponent } from './special/tree-preview/tree-payload/tree-payload.component';
import { NumberStatusComponent } from './default/number-status/number-status.component';
import { EarningStatusComponent } from './default/earning-status/earning-status.component';
import { FinancePaymentStatusComponent } from './default/finance-payment-status/finance-payment-status.component';
import { DateIntervalModule } from '../date-interval/date-interval.module';
import { DateIntervalComponent } from '../date-interval/date-interval.component';
import { FinanceInvoiceStatusComponent } from './default/finance-invoice-status/finance-invoice-status.component';
import { StatusHistoryComponent } from './default/status-history/status-history.component';
import { EarningHistoryComponent } from './default/earnings-history/earning-history.component';
import { InvoiceHistoryComponent } from './default/invoice-history/invoice-history.component';
import { PaymentHistoryComponent } from './default/payment-history/payment-history.component';
import { DateFilterComponent } from './default/date-filter/date-filter.component';
import { AttachmentsComponent } from './special/attachments/attachments.component';
import { PaymentMethodStatusComponent } from './default/payment-method-status/payment-method-status.component';
import { PaymentMethodTypeComponent } from './special/payment-method-type/payment-method-type.component';
import { ConditionsComponent } from './default/conditions/conditions.component';
import { AffiliateRequestStatusHistoryComponent } from './default/affiliate-request-status-history/affiliate-request-status-history.component';
import { AffiliateRequestStatusComponent } from './default/affiliate-request-status/affiliate-request-status.component';
import { CrmQuestionsLinkComponent } from './default/crm-questions-link/crm-questions-link.component';
import { RegionComponent } from './default/region/region.component';
import { CountryRegionComponent } from './default/country-region/country-region.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { RemovePrefixPipe } from './default/country-region/remove-prefix.pipe';
import { UrlReplacingSearchModule } from './default/urls-replacing-search/url-replacing-search.module';
import { ComparingAnalyticsDifferenceTypeComponent } from './special/comparing-analytics-difference-type/comparing-analytics-difference-type.component';
import { ComparingNumberComponent } from './special/comparing-number/comparing-number.component';
import { ComparingPercentageComponent } from './special/comparing-percentage/comparing-percentage.component';
import { ReferralPayoutsComponent } from './special/referral-payouts/referral-payouts.component';
import { EmptyModule } from '../empty/empty.module';
import { OfferDescriptionComponent } from './special/offer-description/offer-description.component';
import { MoneyWithCurrencyComponent } from './special/money-with-currency/money-with-currency.component';
import { EarningConvertErrorComponent } from './special/earning-convert-error/earning-convert-error.component';
import { AffiliateClusterLinkWithAllComponent } from './default/affiliate-cluster-link-with-all/affiliate-cluster-link-with-all.component';
import { TokenStatusComponent } from './default/token-status/token-status.component';
import { TokenTypeComponent } from './default/token-type/token-type.component';
import { CurrencySelectComponent } from './special/currency-select/currency-select.component';
import { OfferVerticalComponent } from './default/offer-vertical/offer-vertical.component';
import { NumberFormatPipe } from '../../pipes/number-format/number-format.pipe';
import { BitcoinFormatPipe } from '../../pipes/bitcoin-format/bitcoin-format';
import { LinkWithCopyComponent } from './default/link-with-copy/link-with-copy.component';
import { LogMethodsComponent } from './default/log-methods/log-methods.component';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    P500UIButtonsModule,
    P500UIIconsModule,
    P500UITooltipModule,
    P500UIPipesModule,
    RouterModule,
    P500UIFormFieldsModule,
    ActionButtonDirectiveModule,
    P500UIManyItemsModule,
    RbacDirectiveModule,
    NotificationDescriptionModule,
    DateIntervalModule,
    NgSelectModule,
    UrlReplacingSearchModule,
    EmptyModule,
    NgOptimizedImage,
    NumberFormatPipe,
    BitcoinFormatPipe,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
  ],
  declarations: [
    DynamicComponent,
    StringComponent,
    StringOrEmptyComponent,
    StringOrDefaultComponent,
    BooleanComponent,
    DatetimeComponent,
    MoneyComponent,
    DefaultEmptyComponent,
    LinkComponent,
    StringWhitespaceOrEmptyComponent,
    ManyItemsComponent,
    CountryComponent,
    CountriesComponent,
    ConversionTrackingTypeComponent,
    GoalRevenueTypeComponent,
    GoalPayoutTypeComponent,
    AffiliatesTrackersTypeComponent,
    OfferConversionTypeComponent,
    CountriesSelectComponent,
    ReportingPresetsDrilldownModalComponent,
    ReportingPresetsReportBuilderModalComponent,
    StringWhitespaceOrEmptyWithCopyComponent,
    StatusComponent,
    ViewModeComponent,
    AffiliatePdfComponent,
    InvoiceStatusComponent,
    CurrencyComponent,
    LinksComponent,
    OfferUrlSourceComponent,
    StringListProtectedComponent,
    AuthAsComponent,
    ProtectedFieldComponent,
    NotificationTypeComponent,
    IncidentStatusComponent,
    AuthorComponent,
    NumberFieldDynamicHintComponent,
    BusinessFormatComponent,
    BusinessModelFormatComponent,
    LanguageComponent,
    NullableSelectComponent,
    AffiliateLinkWithAllComponent,
    NumberStatusComponent,
    LanguagesComponent,
    CrmCurrencyComponent,
    TreeMoneyFormatComponent,
    BaselineCumulativeComponent,
    AffiliateAnswersComponent,
    NumberComponent,
    PostbackStatusComponent,
    PercentComponent,
    GoalTypesComponent,
    CrmPercentComponent,
    TermsFormatComponent,
    NewPqlTypeFreeComponent,
    TreePayloadComponent,
    EarningStatusComponent,
    FinancePaymentStatusComponent,
    FinanceInvoiceStatusComponent,
    StatusHistoryComponent,
    EarningHistoryComponent,
    InvoiceHistoryComponent,
    PaymentHistoryComponent,
    DateFilterComponent,
    AttachmentsComponent,
    PaymentMethodStatusComponent,
    PaymentMethodTypeComponent,
    AffiliateRequestStatusHistoryComponent,
    AffiliateRequestStatusComponent,
    CrmQuestionsLinkComponent,
    ConditionsComponent,
    RegionComponent,
    CountryRegionComponent,
    RemovePrefixPipe,
    ComparingAnalyticsDifferenceTypeComponent,
    ComparingNumberComponent,
    ComparingPercentageComponent,
    ReferralPayoutsComponent,
    OfferDescriptionComponent,
    MoneyWithCurrencyComponent,
    EarningConvertErrorComponent,
    AffiliateClusterLinkWithAllComponent,
    TokenStatusComponent,
    TokenTypeComponent,
    OfferVerticalComponent,
    CurrencySelectComponent,
    LinkWithCopyComponent,
    LogMethodsComponent,
  ],
  exports: [
    DynamicComponent,
    StringComponent,
    StringOrEmptyComponent,
    StringOrDefaultComponent,
    BooleanComponent,
    DatetimeComponent,
    MoneyComponent,
    DefaultEmptyComponent,
    LinkComponent,
    StringWhitespaceOrEmptyComponent,
    ManyItemsComponent,
    CountryComponent,
    CountriesComponent,
    ConversionTrackingTypeComponent,
    GoalRevenueTypeComponent,
    GoalPayoutTypeComponent,
    AffiliatesTrackersTypeComponent,
    OfferConversionTypeComponent,
    CountriesSelectComponent,
    ReportingPresetsDrilldownModalComponent,
    ReportingPresetsReportBuilderModalComponent,
    StringWhitespaceOrEmptyWithCopyComponent,
    StatusComponent,
    ViewModeComponent,
    AffiliatePdfComponent,
    InvoiceStatusComponent,
    CurrencyComponent,
    LinksComponent,
    OfferUrlSourceComponent,
    StringListProtectedComponent,
    AuthAsComponent,
    ProtectedFieldComponent,
    NotificationTypeComponent,
    IncidentStatusComponent,
    AuthorComponent,
    NumberFieldDynamicHintComponent,
    BusinessFormatComponent,
    BusinessModelFormatComponent,
    LanguageComponent,
    NullableSelectComponent,
    AffiliateLinkWithAllComponent,
    NumberStatusComponent,
    LanguagesComponent,
    CrmCurrencyComponent,
    TreeMoneyFormatComponent,
    BaselineCumulativeComponent,
    AffiliateAnswersComponent,
    NumberComponent,
    PostbackStatusComponent,
    PercentComponent,
    GoalTypesComponent,
    CrmPercentComponent,
    TermsFormatComponent,
    NewPqlTypeFreeComponent,
    TreePayloadComponent,
    DateIntervalComponent,
    FinanceInvoiceStatusComponent,
    FinancePaymentStatusComponent,
    StatusHistoryComponent,
    EarningHistoryComponent,
    InvoiceHistoryComponent,
    PaymentHistoryComponent,
    EarningStatusComponent,
    DateFilterComponent,
    AttachmentsComponent,
    PaymentMethodStatusComponent,
    PaymentMethodTypeComponent,
    ConditionsComponent,
    AffiliateRequestStatusHistoryComponent,
    AffiliateRequestStatusComponent,
    CrmQuestionsLinkComponent,
    ComparingAnalyticsDifferenceTypeComponent,
    ComparingNumberComponent,
    ComparingPercentageComponent,
    AffiliateClusterLinkWithAllComponent,
    TokenStatusComponent,
    TokenTypeComponent,
    OfferVerticalComponent,
    CurrencySelectComponent,
    MoneyWithCurrencyComponent,
    LinkWithCopyComponent,
    LogMethodsComponent,
  ],
})
export class FieldsModule {}
